import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, CheckCircleIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button, Textarea, Input } from "components/ui";
import { getStageAndSubStages } from "services/leadStageService";
import { toast } from "sonner";
import ReactSelect from "react-select";
import { useThemeContext } from "app/contexts/theme/context";

const StageChangeModal = ({ isOpen, onClose, leadData, onStageChange }) => {
  const [formData, setFormData] = useState({
    leadStage: "",
    leadStageId: "",
    subStage: "",
    subStageId: "",
    notes: "",
  });
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const saveRef = useRef(null);
  
  const theme = useThemeContext();
  const isDark = theme?.isDark;

  const customSelectStyles = {
    control: (baseStyles, state) => ({
      ...baseStyles,
      borderColor: state.isFocused 
        ? "var(--color-primary-500, #3b82f6)" 
        : isDark ? "#4b5563" : "#d1d5db",
      backgroundColor: isDark ? "#1f2937" : "#ffffff",
      color: isDark ? "#f3f4f6" : "#1f2937",
      borderRadius: "0.5rem", // rounded-lg
      minHeight: "38px",
      fontSize: "0.875rem", // text-sm
      boxShadow: state.isFocused 
        ? "0 0 0 1px var(--color-primary-500, #3b82f6)" 
        : "none",
      "&:hover": {
        borderColor: isDark ? "#9ca3af" : "#9ca3af",
      },
    }),
    option: (baseStyles, state) => ({
      ...baseStyles,
      backgroundColor: state.isSelected 
        ? "var(--color-primary-500, #3b82f6)" 
        : state.isFocused 
          ? isDark ? "#374151" : "#f3f4f6" 
          : "transparent",
      color: state.isSelected 
        ? "#ffffff" 
        : isDark ? "#f3f4f6" : "#374151",
      cursor: "pointer",
      fontSize: "0.875rem",
      "&:active": {
        backgroundColor: "var(--color-primary-600, #2563eb)",
      }
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      color: isDark ? "#f3f4f6" : "#1f2937",
    }),
    placeholder: (baseStyles) => ({
      ...baseStyles,
      color: isDark ? "#9ca3af" : "#9ca3af",
    }),
    menu: (baseStyles) => ({
      ...baseStyles,
      zIndex: 9999,
      backgroundColor: isDark ? "#1f2937" : "#ffffff",
      borderColor: isDark ? "#4b5563" : "#e5e7eb",
      borderWidth: "1px",
    }),
    menuPortal: (baseStyles) => ({
      ...baseStyles,
      zIndex: 99999,
    }),
  };

  // Load stages when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
      loadStages();
    } else {
      resetForm();
    }
  }, [isOpen, leadData]);

  // Initialize form with leadData stage/substage names after stages are loaded
  useEffect(() => {
    if (isOpen && stages.length > 0) {
      // Find stage using any available property
      const leadStageId = leadData?.leadStageId || leadData?.stageId || leadData?.StageId;
      const leadStageName = leadData?.stage || leadData?.leadStage || leadData?.LeadStage || leadData?.stageName;
      
      const currentStage = stages.find(s => 
        (leadStageId && String(s.stageId) === String(leadStageId)) ||
        (leadStageName && String(s.stageName).toLowerCase() === String(leadStageName).toLowerCase())
      );
      
      if (currentStage) {
        // Initialize form with current lead data
        const leadSubStageId = leadData?.subStageId || leadData?.SubStageId || leadData?.substageId;
        const leadSubStageName = leadData?.subStage || leadData?.SubStageName || leadData?.substage || leadData?.SubstageName;
        
        const subs = currentStage.subStages || currentStage.substages || [];
        
        // Find matching substage using ID or Name
        let currentSubStage = null;
        if (subs.length > 0) {
          currentSubStage = subs.find(s => 
            (leadSubStageId && String(s.subStageId) === String(leadSubStageId)) ||
            (leadSubStageName && String(s.subStageName).toLowerCase() === String(leadSubStageName).toLowerCase()) ||
            (leadSubStageName && String(s.subStageName).toLowerCase().includes(String(leadSubStageName).toLowerCase())) ||
            (leadSubStageName && String(leadSubStageName).toLowerCase().includes(String(s.subStageName).toLowerCase()))
          );
        }

        setFormData(prev => ({
          ...prev,
          leadStage: currentStage.stageName,
          leadStageId: currentStage.stageId,
          subStage: currentSubStage ? currentSubStage.subStageName : "",
          subStageId: currentSubStage ? currentSubStage.subStageId : "",
        }));
      }
    }
  }, [stages, leadData, isOpen]);

  const resetForm = () => {
    setFormData({
      leadStage: "",
      leadStageId: "",
      subStage: "",
      subStageId: "",
      notes: leadData?.remarks || "",
    });
    setIsSubmitting(false);
  };

 const loadStages = async () => {
  try {
    setLoading(true);
    const stagesData = await getStageAndSubStages({ pageSize: 100 });

    const activeSortedStages = (stagesData || [])
      .sort((a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0)); // Ascending order

    setStages(activeSortedStages);
  } catch (error) {
    console.error("Error loading stages:", error);
  } finally {
    setLoading(false);
  }
};


  const handleStageSelect = (opt) => {
    const stageId = opt ? opt.value : "";
    const stage = stages.find(s => String(s.stageId) === String(stageId));
    
    if (stage) {
      setFormData(prev => ({
        ...prev,
        leadStage: stage.stageName,
        leadStageId: stage.stageId,
        subStage: "",
        subStageId: "",
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        leadStage: "",
        leadStageId: "",
        subStage: "",
        subStageId: "",
      }));
    }
  };

  const handleSubStageSelect = (opt) => {
    const subStageId = opt ? opt.value : "";
    const currentStage = stages.find(s => String(s.stageId) === String(formData.leadStageId));
    const subs = currentStage?.subStages || currentStage?.substages || [];
    const subStage = subs.find(s => String(s.subStageId) === String(subStageId));
    
    if (subStage) {
      setFormData(prev => ({
        ...prev,
        subStage: subStage.subStageName,
        subStageId: subStage.subStageId,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        subStage: "",
        subStageId: "",
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.leadStage || !formData.leadStageId) {
      toast.info("Please select a lead stage");
      return;
    }


    setIsSubmitting(true);
    try {
      const result = await onStageChange({
        ...formData,
        stageId: formData.leadStageId,
        subStageId: formData.subStageId || null,
        leadId: leadData?.id,
      });
      
      if (result !== false) {
        onClose();
      }
    } catch (error) {
      console.error("Error updating stage:", error);
      toast.error("Failed to update stage");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get available substages
  const currentStage = stages.find(s => String(s.stageId) === String(formData.leadStageId));
  const availableSubStages = currentStage?.subStages || currentStage?.substages || [];
  
  const stageOptions = stages.map((stage) => ({
    value: String(stage.stageId),
    label: stage.stageName,
  }));

  const subStageOptions = availableSubStages
    .sort((a, b) => (a.subStageOrder ?? 0) - (b.subStageOrder ?? 0))
    .map((sub) => ({
      value: String(sub.subStageId),
      label: sub.subStageName,
    }));



  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[99998] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={()=>{}}
        initialFocus={saveRef}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-lg origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none">
            <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 dark:bg-dark-800 sm:px-5">
              <DialogTitle
                as="h3"
                className="flex items-center gap-2 text-base font-medium text-gray-800 dark:text-dark-100"
              >
                <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                Change Lead Stage
              </DialogTitle>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            <div className="flex flex-col overflow-y-auto px-4 py-4 sm:px-5">
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                Update the stage for {leadData?.name || "this lead"} to track progress.
              </p>

              
              <div className="space-y-5">
                {/* Lead Stage */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Lead Stage *
                  </label>
                  <ReactSelect
                    options={stageOptions}
                    value={stageOptions.find(opt => opt.value === String(formData.leadStageId)) || null}
                    onChange={handleStageSelect}
                    placeholder="Select Lead Stage"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isDisabled={loading || isSubmitting}
                    isClearable
                    isSearchable={false}
                    styles={customSelectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  />
                </div>

                {/* Sub Stage */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Sub Stage (Optional)
                  </label>
                  <ReactSelect
                    options={subStageOptions}
                    value={subStageOptions.find(opt => opt.value === String(formData.subStageId || "")) || null}
                    onChange={handleSubStageSelect}
                    placeholder={
                      !formData.leadStageId 
                        ? "Select a stage first" 
                        : availableSubStages.length === 0 
                          ? "No sub-stages available" 
                          : "Select Sub Stage (Optional)"
                    }
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isDisabled={isSubmitting || !formData.leadStageId || availableSubStages.length === 0}
                    isClearable
                    isSearchable={false}
                    styles={customSelectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  />
                </div>

                {/* Notes */}
                <Textarea
                  label="Notes (Optional)"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  placeholder="Add notes about this stage change..."
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="mt-6 space-x-3 text-end">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  className="min-w-[7rem] rounded-full"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  color="primary"
                  ref={saveRef}
                  className="min-w-[7rem] rounded-full"
                  disabled={loading || isSubmitting || !formData.leadStage}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

export default StageChangeModal;
